import styled from '@emotion/styled';

import { css } from '@emotion/react';
import { Icon, Pill } from '@gumtree/ui-library';
import {
    fontSizes,
    colors,
    colorVariables,
    lineHeights,
    gutterSizes,
    fontWeights,
} from '@gumtree/ui-library/src/base/theme';
import { StarSize } from './constants';

const shouldForwardProp = (p: string) => !['isMobile', 'newFlow', 'hasInteracted'].includes(p);

export const StyledInteractiveBox = styled('div', {
    shouldForwardProp,
})<{
    newFlow?: boolean;
}>`
    position: relative;
    display: flex;
    flex-direction: column;
    grid-gap: ${gutterSizes.large};

    ${({ newFlow }) =>
        newFlow &&
        `
      padding-top: ${gutterSizes.base};
      grid: 0;
    `}
`;

export const StyledIcon = styled(Icon)<{ size: keyof typeof StarSize }>`
    font-size: ${({ size }) => StarSize[size]};
    min-width: unset;
`;

export const StyledWrapper = styled.div<{ isMobile: boolean }>`
    display: flex;
    align-items: center;
    box-sizing: border-box;
    ${({ isMobile }) =>
        !isMobile &&
        `
    margin-bottom: 6px;
  `};
`;

export const StyledStarsWrapper = styled('div', {
    shouldForwardProp,
})<{
    newFlow?: boolean;
}>`
    display: flex;
    justify-content: center;

    .star-button {
        background: none;
        border: none;
        padding: 0;
    }

    ${({ newFlow }) =>
        newFlow &&
        `
        ${StyledIcon} {
          cursor: pointer;
        }

        .star-button {
            padding: ${gutterSizes.xsmall};
        }
    `}
`;

export const TextWrapper = styled('div', {
    shouldForwardProp,
})<{
    hasInteracted: boolean;
}>`
    visibility: hidden;
    margin: ${gutterSizes.medium} 0 ${gutterSizes.large} 0;

    ${({ hasInteracted }) =>
        hasInteracted &&
        `
        visibility: visible;
    `}
`;

export const StyledFeedbackContainer = styled('div', {
    shouldForwardProp,
})<{
    newFlow?: boolean;
}>`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    ${({ newFlow }) =>
        newFlow &&
        `
      padding: ${gutterSizes.base} 0 ${gutterSizes.base} 0;
  `}
`;

export const StyledPill = styled(Pill, {
    shouldForwardProp,
})<{
    newFlow?: boolean;
}>`
    ${({ newFlow }) =>
        newFlow
            ? `
        background-color: ${colors.white};
        border: 1px solid ${colorVariables.borderLight};
        color: ${colors.blade};
        margin-right: ${gutterSizes.medium};
        margin-bottom: ${gutterSizes.medium};

        .icon {
            font-size: ${fontSizes.smaller};
            position: relative;
            right: -${gutterSizes.small};
        }

        .pill-title {
          padding: ${gutterSizes.small} 6px;
          font-size: ${fontSizes.base};
        }

        &.active, &:hover {
          background: ${colors.bgInfo};
          color: ${colors.blade};
          border: 1px solid ${colors.bgInfo};
        }

        &:disabled {
            background: ${colorVariables.disabledBackground};
            color: ${colorVariables.disabledText};
        }
    `
            : `
    background: ${colors.branch10};
    transition: 0.3s;
      &.active {
        background: ${colors.branch40};
      }
      &:hover {
        background: ${colors.branch40};
      }
    `}
`;

export const StyledAverageRating = styled.span`
    font-size: ${fontSizes.small};
    font-weight: ${fontWeights.bold};
    color: ${colors.blade};
    padding: 2px 5px 0 0;
`;

export const StyledTotal = styled.span<{ isMobile: boolean }>`
    padding-top: 2px;
    font-size: ${fontSizes.small};
    color: ${colors.bark80};
    padding-left: 2px; // to equal 5px total
    letter-spacing: 0.5px;
`;

export const submitButtonCss = (theme) => css`
    && {
        box-sizing: border-box;
        display: block;
        width: 100%;
        padding: ${gutterSizes.medium};
        background-color: ${theme.palette.primary.main};
        border: 1px solid transparent;
        font-size: ${fontSizes.medium};
        line-height: ${lineHeights.small};
        color: ${theme.palette.primary.mainContrastText};
        cursor: pointer;

        &:hover {
            background-color: ${theme.palette.primary.dark};
            color: ${theme.palette.primary.darkContrastText};
        }

        transition: background-color 0.2s ease-out;
    }
`;

export const StyledLink = styled.a`
    display: flex;
    align-items: center;
    &:hover {
        span:not(.icon) {
            text-decoration: underline;
        }
    }
`;

export const continueButtonCss = (theme, hasInteracted) => css`
    && {
        box-sizing: border-box;
        display: flex;
        align-self: start;
        justify-content: center;
        min-width: 100px;
        padding: 12px;
        border: 1px solid transparent;
        font-size: ${fontSizes.base};
        line-height: ${lineHeights.small};
        font-weight: ${fontWeights.bold};
        cursor: pointer;
        border-radius: ${gutterSizes.small};
        margin: 0 auto;

        &:disabled {
            background: ${theme.palette.disabled.main};
            color: ${theme.palette.disabled.mainContrastText};
            border: ${theme.palette.disabled.border};
        }

        ${hasInteracted
            ? `
        background-color: ${theme.palette.primary.main};
        color: ${theme.palette.primary.mainContrastText};
        `
            : `
        background-color: ${theme.palette.secondary.mainndLight};
        color: ${theme.palette.secondary.mainContrastText};
        `}
    }
`;
